import { useEffect } from 'react'
import { useIsServerEffect } from '@overdose/components'
import classNames from 'classnames'
import Head from 'next/head'
import { usePage } from '~/hooks'
import { DEFAULT_COUNTRY_CODE_NZ } from '~/constants/countries'
import styles from './ProductMain.module.css'
import { LatitudeGemPayLaterWidgetProps } from './ProductMain.types'

// Latitude Widget has a Learn More Link without an href tag and uses the onclick attribute instead.
// This causes an accessibility issue where href is missing for anchor tags
// Below Script replaces the anchor tag with a button and also adds a fixed width to the brand image.
function onload(countryCode: string) {
  const container = document.querySelector('#latitude-banner-container')
  const imageEl = container.getElementsByTagName('img')[0]

  if (imageEl) {
    imageEl.setAttribute('width', countryCode === 'AU' ? '85px' : '57px')
    imageEl.setAttribute('height', '17px')
  }

  const learnMoreAnchorEl = container.getElementsByTagName('a')[0]

  if (!learnMoreAnchorEl) {
    return
  }
  const buttonEl = document.createElement('button')
  const textNode = document.createTextNode('Learn more')
  buttonEl.appendChild(textNode)
  buttonEl.setAttribute('onclick', learnMoreAnchorEl.getAttribute('onClick'))
  buttonEl.setAttribute('style', learnMoreAnchorEl.getAttribute('style'))

  learnMoreAnchorEl.replaceWith(buttonEl)
}

export const LatitudeGemPayLaterWidget = ({
  offer,
}: LatitudeGemPayLaterWidgetProps) => {
  const url = `https://checkout.latitudefinancial.com/assets/content.js?platform=direct&merchantId=${offer.merchantId}`
  const isServer = useIsServerEffect()
  const page = usePage()
  const isNZSite = page.countryCode === DEFAULT_COUNTRY_CODE_NZ
  const { price } = offer

  if (!isServer) {
    window.LatitudeCheckout = {
      ...window?.LatitudeCheckout,
      page: 'product',
      container: 'latitude-banner-container',
      layout: 'inversed',
      ...offer,
    }
  }

  useEffect(() => {
    // Fetches the script every time the route changes to be a PDP.
    // This approach has been implemented as the Script Component from Next.js was not remounting the widget after a route change.
    const script = document.createElement('script')
    script.id = 'latitude-gem-bnpl-script'
    script.src = `https://checkout.latitudefinancial.com/assets/content.js?platform=direct&merchantId=${offer.merchantId}`
    script.onload = () => {
      onload(page.countryCode)
    }
    script.defer = true
    document.documentElement.append(script)

    return () => {
      const scriptToRemove = document.querySelector('#latitude-gem-bnpl-script')
      scriptToRemove && document.documentElement.removeChild(scriptToRemove)
    }
  }, [offer.merchantId, page.countryCode])

  const isOverPriceThreshold = Number(price) >= Number(offer.minAmount || '250')

  return (
    <div
      className={classNames('h-full', {
        [styles.latitudePayLater]: !isNZSite && !isOverPriceThreshold,
        [styles.gemPayLater]: isNZSite && !isOverPriceThreshold,
        [styles.gemPayLater_price_over_threshold]:
          isNZSite && isOverPriceThreshold,
        [styles.latitudePayLater_price_over_threshold]:
          !isNZSite && isOverPriceThreshold,
      })}>
      <Head>
        <link rel='dns-prefetch' href={url} />
        <link rel='prefetch' href={url} as='script' />
      </Head>

      <div id='latitude-banner-container' />
    </div>
  )
}
